import { ReactNode } from "react";
import { Plus } from "lucide-react";
import {
  CommandGroup,
  CommandItem,
  CommandSeparator,
  CommandShortcut,
} from "@/components/ui/command";

/** A pinned create action rendered at the bottom of the selector dropdown. */
export interface EntityCreateRow {
  /** Stable key for the row. */
  key: string;
  /** Builds the row label from the current search query. */
  label: (query: string) => string;
  /** Optional leading icon, defaults to a plus. */
  icon?: ReactNode;
  /** Invoked with the current search query and a callback that closes the selector. */
  onSelect: (query: string, close: () => void) => void;
  /** Renders a keyboard hint and binds cmd/ctrl+enter when set to "mod+enter". */
  shortcut?: "mod+enter";
}

interface EntitySelectorCreateRowsProps {
  /** The create actions to render. */
  rows: EntityCreateRow[];
  /** The current search query, passed to each row's label and select handler. */
  query: string;
  /** Closes the popover and clears the search, handed to each row's select handler. */
  closeSelector: () => void;
}

/**
 * Renders the pinned create actions below a divider at the bottom of the dropdown.
 * @param props - The create rows, the current search query and the close callback
 * @returns The create row group, or null when there are no rows
 */
export function EntitySelectorCreateRows({
  rows,
  query,
  closeSelector,
}: EntitySelectorCreateRowsProps) {
  if (rows.length === 0) {
    return null;
  }

  return (
    <>
      <CommandSeparator />
      <CommandGroup>
        {rows.map((row) => (
          <CommandItem
            key={row.key}
            value={`create-row:${row.key}`}
            onSelect={() => row.onSelect(query, closeSelector)}
          >
            {row.icon ?? <Plus className="mr-2 h-4 w-4" />}
            <span className="truncate">{row.label(query)}</span>
            {row.shortcut === "mod+enter" && (
              <CommandShortcut>⌘↵</CommandShortcut>
            )}
          </CommandItem>
        ))}
      </CommandGroup>
    </>
  );
}
